
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { ContentStats } from './AdminContentDashboard';
import InvitationMetricsCard from './InvitationMetricsCard';

interface ReviewsOverviewProps {
  loading: boolean;
  stats?: ContentStats['reviews'];
}

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe'];

const ReviewsOverview: React.FC<ReviewsOverviewProps> = ({ loading, stats }) => {
  // Prepare data for review status distribution
  const reviewStatusData = stats ? [
    { name: 'Published', value: stats.published },
    { name: 'Draft', value: stats.draft }
  ] : [];

  // Prepare data for papers distribution by review count
  const distributionData = stats?.paperDistribution ? 
    Object.entries(stats.paperDistribution)
      .map(([reviewCount, paperCount]) => ({ 
        name: `${reviewCount} ${reviewCount === '1' ? 'review' : 'reviews'}`,
        value: paperCount
      })) : [];

  return (
    <div className="space-y-6">
      <InvitationMetricsCard />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="col-span-1 w-full">
          <CardHeader>
            <CardTitle className="text-lg">Review Status</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={reviewStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {reviewStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Paper Distribution by Review Count */}
        <Card className="col-span-1 md:col-span-2 w-full">
          <CardHeader>
            <CardTitle className="text-lg">Papers by Review Count</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[350px] w-full" />
            ) : (
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={distributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" name="Papers" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
                {stats?.averagePerPaper !== undefined && (
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    Average reviews per paper: {stats.averagePerPaper.toFixed(2)}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReviewsOverview;
